import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionContext";
import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { BrandMark } from "@/components/brand/BrandMark";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Sheet, SheetClose, SheetContent } from "@/components/ui/sheet";
import {
  LayoutDashboard, Users, CalendarClock, ClipboardList,
  FolderOpen, Megaphone, UserCog, LogOut, Network, Layers, UserPlus, ShieldCheck,
  MoreHorizontal, X, HelpCircle, Bell, BarChart3, FileText, ClipboardCheck, ChevronDown, Cog, BookOpenCheck,
  PanelLeftClose, PanelLeftOpen, ScrollText
} from "lucide-react";

const EVAL_KEYS = ["eval.view_results", "eval.create", "eval.edit", "eval.lifecycle", "eval.grant_attempt"];

const DASHBOARD = { id: "dashboard", to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["MASTER", "ADMIN", "PROFESSOR"] };
const HELP = { id: "help", to: "/help", label: "Ajuda", icon: HelpCircle, roles: ["MASTER", "ADMIN", "PROFESSOR"] };

const GROUPS = [
  {
    id: "academico", label: "Acadêmico", icon: BookOpenCheck, items: [
      { id: "students", to: "/students", label: "Alunos", icon: Users, roles: ["MASTER", "ADMIN", "PROFESSOR"] },
      { id: "classes", to: "/classes", label: "Turmas", icon: Layers, perms: ["manage.classes"] },
      { id: "lessons", to: "/lessons", label: "Aulas", icon: CalendarClock, roles: ["MASTER", "ADMIN", "PROFESSOR"] },
      { id: "tasks", to: "/tasks", label: "Tarefas", icon: ClipboardList, roles: ["MASTER", "ADMIN", "PROFESSOR"] },
      { id: "materials", to: "/materials", label: "Materiais", icon: FolderOpen, roles: ["MASTER", "ADMIN", "PROFESSOR"] },
      { id: "evaluations", to: "/evaluations", label: "Avaliações", icon: FileText, perms: EVAL_KEYS },
      { id: "grading", to: "/grading", label: "Correções", icon: ClipboardCheck, perms: ["eval.grade"] },
    ],
  },
  {
    id: "comunicacao", label: "Comunicação", icon: Megaphone, items: [
      { id: "notices", to: "/notices", label: "Avisos", icon: Megaphone, roles: ["MASTER", "ADMIN", "PROFESSOR"] },
      { id: "notifications", to: "/notifications", label: "Notificações", icon: Bell, perms: ["content.push"] },
    ],
  },
  {
    id: "gestao", label: "Gestão", icon: BarChart3, items: [
      { id: "reports", to: "/reports", label: "Relatórios", icon: BarChart3, perms: ["view.reports"] },
      { id: "registrations", to: "/registrations", label: "Inscrições", icon: UserPlus, perms: ["manage.students"], badge: "pending" },
      { id: "cells", to: "/cells", label: "Células", icon: Network, perms: ["manage.cells"] },
      { id: "users", to: "/users", label: "Usuários", icon: UserCog, perms: ["manage.users"] },
    ],
  },
  {
    id: "sistema", label: "Sistema", icon: Cog, items: [
      { id: "audit", to: "/audit", label: "Auditoria", icon: ScrollText, perms: ["view.audit"] },
      { id: "permissions", to: "/permissions", label: "Permissões", icon: ShieldCheck, roles: ["MASTER"] },
    ],
  },
];

const MOBILE_PRIMARY_IDS = ["dashboard", "students", "lessons", "tasks"];
const COLLAPSE_STORAGE_KEY = "doxa_sidebar_collapsed";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const nav = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const [pendingCount, setPendingCount] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_STORAGE_KEY) === "1"; } catch { return false; }
  });

  const canSee = useCallback((item) => {
    if (item.roles) return item.roles.includes(user?.role);
    if (item.perms) return user?.role === "MASTER" || item.perms.some((key) => can(key));
    return true;
  }, [user, can]);

  const visibleDashboard = canSee(DASHBOARD);
  const visibleHelp = canSee(HELP);
  const visibleGroups = useMemo(() => (
    GROUPS.map((group) => ({ ...group, items: group.items.filter(canSee) })).filter((group) => group.items.length > 0)
  ), [canSee]);
  const mobileMoreGroups = useMemo(() => (
    visibleGroups
      .map((group) => ({ ...group, items: group.items.filter((item) => !MOBILE_PRIMARY_IDS.includes(item.id)) }))
      .filter((group) => group.items.length > 0)
  ), [visibleGroups]);

  const isActivePath = useCallback((to) => (
    to === "/dashboard" ? pathname === "/dashboard" : (pathname === to || pathname.startsWith(to + "/"))
  ), [pathname]);

  const activeGroupId = useMemo(() => {
    const group = visibleGroups.find((row) => row.items.some((item) => isActivePath(item.to)));
    return group ? group.id : null;
  }, [visibleGroups, isActivePath]);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      try { localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const [mobileOpenGroup, setMobileOpenGroup] = useState(null);
  useEffect(() => { if (showMore) setMobileOpenGroup(activeGroupId); }, [showMore, activeGroupId]);

  const canSeePending = user?.role === "MASTER" || can("manage.students");
  const fetchPending = useCallback(() => {
    if (!canSeePending) return;
    api.get("/registrations/pending-count").then(({ data }) => setPendingCount(data.count || 0)).catch(() => {});
  }, [canSeePending]);
  useEffect(() => { fetchPending(); }, [fetchPending, location.pathname]);
  useEffect(() => {
    const handler = () => fetchPending();
    window.addEventListener("doxa:pending-changed", handler);
    return () => window.removeEventListener("doxa:pending-changed", handler);
  }, [fetchPending]);

  const badgeFor = (item) => (item.badge === "pending" && pendingCount > 0 ? pendingCount : null);
  const groupBadge = (group) => group.items.reduce((acc, item) => acc + (badgeFor(item) || 0), 0);
  const onLogout = async () => { await logout(); nav("/login"); };
  const userInitial = user?.name?.trim()?.charAt(0)?.toUpperCase() || "D";

  const ItemLink = ({ it, onClick }) => {
    const badge = badgeFor(it);
    return (
      <NavLink
        to={it.to}
        onClick={onClick}
        className={({ isActive }) => `doxa-sidebar-link ${isActive ? "active" : ""}`}
        data-testid={`nav-${it.to.slice(1)}`}
      >
        <it.icon className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-[13px]">{it.label}</span>
        {badge != null && <span className="ml-auto rounded-full bg-[#F75A1B] px-2 py-0.5 text-[10px] font-semibold leading-none text-white" data-testid={`nav-badge-${it.id}`}>{badge}</span>}
      </NavLink>
    );
  };

  const SectionDivider = ({ label }) => (
    <div className="flex items-center gap-2 px-2 pt-2.5 pb-1" aria-hidden="true">
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#8A8378]">{label}</span>
      <span className="h-px flex-1 bg-[#E8E1D6]" />
    </div>
  );

  const CollapsedItem = ({ it }) => {
    const badge = badgeFor(it);
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink
            to={it.to}
            className={({ isActive }) => `relative mx-auto flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${isActive ? "bg-[#F75A1B] text-white shadow-[0_4px_12px_rgba(247,90,27,0.22)]" : "text-[#4A4640] hover:bg-[#FFF0E9] hover:text-[#D94A12]"}`}
            data-testid={`nav-collapsed-${it.to.slice(1)}`}
          >
            <it.icon className="h-[18px] w-[18px]" />
            {badge != null && <span className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#F75A1B] px-1 text-[9px] font-semibold text-white ring-2 ring-white" data-testid={`nav-collapsed-badge-${it.id}`}>{badge}</span>}
          </NavLink>
        </TooltipTrigger>
        <TooltipContent side="right">{it.label}</TooltipContent>
      </Tooltip>
    );
  };

  const collapsedSections = [
    ...(visibleDashboard ? [{ id: "principal", items: [DASHBOARD] }] : []),
    ...visibleGroups,
  ];

  return (
    <div className="flex min-h-screen bg-[#F8F3EA]">
      <TooltipProvider delayDuration={150}>
        <aside className={`hidden lg:flex ${collapsed ? "w-[72px]" : "w-[244px]"} sticky top-0 h-screen flex-col border-r border-[#E0D9CD] bg-white transition-[width] duration-200`} data-testid="admin-sidebar-aside" data-collapsed={collapsed ? "1" : "0"}>
          <div className={`shrink-0 border-b border-[#EBE5DB] ${collapsed ? "px-2.5 py-3" : "px-4 py-4"}`}>
            <div className={`flex items-center ${collapsed ? "flex-col gap-2" : "gap-2.5"}`}>
              <BrandMark size="md" />
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-[17px] font-bold leading-tight">Doxa School</p>
                  <p className="text-[11px] text-[#6B655E]">Sistema interno</p>
                </div>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={toggleCollapsed}
                    aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[#6B655E] transition-colors hover:bg-[#FFF0E9] hover:text-[#D94A12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/40"
                    data-testid="sidebar-collapse-toggle"
                  >
                    {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{collapsed ? "Expandir menu" : "Recolher menu"}</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {collapsed ? (
            <nav className="flex-1 overflow-y-auto px-2 py-2.5 scrollbar-hide" data-testid="admin-sidebar">
              {collapsedSections.map((section, sectionIndex) => (
                <div key={section.id} className={sectionIndex ? "mt-2 border-t border-[#EEE7DD] pt-2" : ""}>
                  <div className="space-y-1">{section.items.map((item) => <CollapsedItem key={item.id} it={item} />)}</div>
                </div>
              ))}
            </nav>
          ) : (
            <nav className="flex-1 overflow-y-auto px-3 py-2.5 scrollbar-hide" data-testid="admin-sidebar">
              {visibleDashboard && <div className="pb-1"><ItemLink it={DASHBOARD} /></div>}
              {visibleGroups.map((group) => (
                <section key={group.id} aria-label={group.label}>
                  <SectionDivider label={group.label} />
                  <div className="space-y-0.5">
                    {group.items.map((item) => <ItemLink key={item.id} it={item} />)}
                  </div>
                </section>
              ))}
            </nav>
          )}

          <div className={`shrink-0 border-t border-[#EBE5DB] ${collapsed ? "p-2" : "px-3 py-2.5"}`}>
            {collapsed ? (
              <div className="space-y-1">
                {visibleHelp && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <NavLink to="/help" className={({ isActive }) => `mx-auto flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${isActive ? "bg-[#F75A1B] text-white" : "text-[#4A4640] hover:bg-[#FFF0E9] hover:text-[#D94A12]"}`} data-testid="nav-collapsed-help"><HelpCircle className="h-[18px] w-[18px]" /></NavLink>
                    </TooltipTrigger>
                    <TooltipContent side="right">Ajuda</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button onClick={onLogout} data-testid="logout-btn" className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl text-[#4A4640] transition-colors hover:bg-[#FFF0E9] hover:text-[#D94A12]"><LogOut className="h-[18px] w-[18px]" /></button>
                  </TooltipTrigger>
                  <TooltipContent side="right">{user?.name} · Sair</TooltipContent>
                </Tooltip>
              </div>
            ) : (
              <>
                {visibleHelp && <ItemLink it={HELP} />}
                <div className="mx-1 mt-1 flex items-center gap-2.5 rounded-xl bg-[#FAF7F1] px-2.5 py-2">
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#FFF0E9] font-display text-xs font-bold text-[#D94A12]">{userInitial}</div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold">{user?.name}</p>
                    <p className="text-[10px] uppercase tracking-wide text-[#8A8378]">{user?.role}</p>
                  </div>
                </div>
                <button onClick={onLogout} data-testid="logout-btn" className="mt-1 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-xs text-[#4A4640] transition-colors hover:bg-[#FFF0E9] hover:text-[#D94A12]">
                  <LogOut className="h-4 w-4" /> Sair
                </button>
              </>
            )}
          </div>
        </aside>
      </TooltipProvider>

      <div className="fixed inset-x-0 top-0 z-30 border-b border-[#E0D9CD] bg-white/92 backdrop-blur-xl lg:hidden" style={{ paddingTop: "env(safe-area-inset-top)" }} data-testid="mobile-app-bar">
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-2.5">
            <BrandMark size="sm" />
            <span className="font-display text-[17px] font-bold">Doxa School</span>
          </div>
          <button
            type="button"
            onClick={() => setShowMore(true)}
            aria-label="Abrir menu e conta"
            className="grid h-10 w-10 place-items-center rounded-full bg-[#FFF0E9] font-display text-sm font-bold text-[#D94A12] ring-1 ring-[#F75A1B]/10 transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/40"
            data-testid="mobile-account-menu"
          >
            {userInitial}
          </button>
        </div>
      </div>

      <main className="flex-1 overflow-x-hidden pt-[calc(3.5rem+env(safe-area-inset-top))] pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-0 lg:pt-0">
        <Outlet />
      </main>

      {(() => {
        const primaries = MOBILE_PRIMARY_IDS
          .map((id) => (id === "dashboard" ? (visibleDashboard ? DASHBOARD : null) : GROUPS.flatMap((group) => group.items).find((item) => item.id === id)))
          .filter((item) => item && canSee(item));
        const morePending = mobileMoreGroups.reduce((acc, group) => acc + groupBadge(group), 0);
        const anyMoreActive = mobileMoreGroups.some((group) => group.items.some((item) => isActivePath(item.to))) || (visibleHelp && isActivePath("/help"));
        return (
          <>
            <nav className="fixed inset-x-0 bottom-0 z-30 flex min-h-[64px] items-start justify-around border-t border-[#E0D9CD] bg-white/94 px-2 pt-1.5 backdrop-blur-xl lg:hidden" style={{ paddingBottom: "max(0.45rem, env(safe-area-inset-bottom))" }} data-testid="mobile-bottom-nav">
              {primaries.map((item) => {
                const badge = badgeFor(item);
                return (
                  <NavLink key={item.id} to={item.to} className={({ isActive }) =>
                    `relative flex min-h-[50px] min-w-[58px] flex-col items-center justify-center gap-0.5 rounded-2xl px-2 py-1.5 text-xs transition active:scale-[0.97] ${isActive ? "bg-[#FFF0E9] text-[#D94A12]" : "text-[#6B655E]"}`
                  } data-testid={`bottomnav-${item.to.slice(1)}`}>
                    <item.icon className="h-5 w-5" strokeWidth={2.1} />
                    <span className="text-[10px] font-medium">{item.label}</span>
                    {badge != null && <span className="absolute right-1.5 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#F75A1B] px-1 text-[9px] font-semibold text-white">{badge}</span>}
                  </NavLink>
                );
              })}
              <button onClick={() => setShowMore(true)} data-testid="bottomnav-more"
                className={`relative flex min-h-[50px] min-w-[58px] flex-col items-center justify-center gap-0.5 rounded-2xl px-2 py-1.5 text-xs transition active:scale-[0.97] ${anyMoreActive ? "bg-[#FFF0E9] text-[#D94A12]" : "text-[#6B655E]"}`}>
                <MoreHorizontal className="h-5 w-5" strokeWidth={2.1} />
                <span className="text-[10px] font-medium">Menu</span>
                {morePending > 0 && <span className="absolute right-1.5 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#F75A1B] px-1 text-[9px] font-semibold text-white">{morePending}</span>}
              </button>
            </nav>

            <Sheet open={showMore} onOpenChange={setShowMore}>
              <SheetContent
                side="bottom"
                className="max-h-[88dvh] gap-0 rounded-t-[28px] border-x-0 border-b-0 border-t border-[#E0D9CD] bg-white p-0 shadow-[0_-18px_48px_rgba(17,17,17,0.14)] [&>button]:hidden lg:hidden"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
                data-testid="more-sheet"
              >
                <div className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-[#D7D0C5]" aria-hidden="true" />
                <div className="flex shrink-0 items-center justify-between px-4 pb-3 pt-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F75A1B]">Doxa School</p>
                    <h2 className="font-display text-xl font-bold">Menu</h2>
                  </div>
                  <SheetClose asChild>
                    <button data-testid="more-sheet-close" aria-label="Fechar menu" className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F6EFE4] text-[#4A4640] transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/35">
                      <X className="h-5 w-5" />
                    </button>
                  </SheetClose>
                </div>

                <div className="mx-4 mb-3 flex shrink-0 items-center gap-3 rounded-2xl border border-[#EBE5DB] bg-[#FAF7F1] p-3.5" data-testid="mobile-account-card">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#FFF0E9] font-display text-sm font-bold text-[#D94A12]">{userInitial}</div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[#1F1F1F]">{user?.name}</p>
                    <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#8A8378]">{user?.role}</p>
                  </div>
                </div>

                <div className="flex-1 space-y-1 overflow-y-auto px-3 pb-3 scrollbar-hide">
                  {mobileMoreGroups.map((group) => {
                    const open = mobileOpenGroup === group.id;
                    const badge = groupBadge(group);
                    const hasActive = group.items.some((item) => isActivePath(item.to));
                    return (
                      <div key={group.id} className="pt-0.5">
                        <button type="button" onClick={() => setMobileOpenGroup(open ? null : group.id)} aria-expanded={open} aria-controls={`m-navgrp-${group.id}`}
                          className="flex min-h-11 w-full items-center gap-2 rounded-xl px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-[#8A8378] transition hover:bg-[#F6EFE4] active:bg-[#F6EFE4]" data-testid={`m-nav-group-${group.id}`}>
                          <group.icon className="h-4 w-4 shrink-0 text-[#B0A79B]" />
                          <span className="flex-1 text-left">{group.label}</span>
                          {!open && badge > 0 && <span className="rounded-full bg-[#F75A1B] px-1.5 py-0.5 text-[10px] font-semibold normal-case leading-none text-white">{badge}</span>}
                          {!open && badge === 0 && hasActive && <span className="h-1.5 w-1.5 rounded-full bg-[#F75A1B]" />}
                          <ChevronDown className={`h-4 w-4 shrink-0 text-[#B0A79B] transition-transform ${open ? "rotate-180" : ""}`} />
                        </button>
                        {open && (
                          <div id={`m-navgrp-${group.id}`} className="mt-0.5 space-y-0.5">
                            {group.items.map((item) => <ItemLink key={item.id} it={item} onClick={() => setShowMore(false)} />)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {visibleHelp && <ItemLink it={HELP} onClick={() => setShowMore(false)} />}
                </div>

                <div className="shrink-0 border-t border-[#EBE5DB] bg-white px-3 pb-3 pt-2.5">
                  <button onClick={onLogout} data-testid="more-logout" className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#F6EFE4] px-3 py-3 text-sm font-semibold text-[#1F1F1F] transition active:scale-[0.99]">
                    <LogOut className="h-4 w-4" /> Sair da conta
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </>
        );
      })()}
    </div>
  );
}